import { useState } from 'react';
import { PaymentButton } from '../components/PaymentButton';
import { AnimatedBarGraph } from '../components/AnimatedBarGraph';

interface DataPoint {
  index: number;
  label: string;
  col1: number;
  [key: string]: number | string;  // Allow dynamic column keys
}

export function AnimatedBarGraphPage() {
  const [data, setData] = useState<DataPoint[]>([]);
  const [inputText, setInputText] = useState('');
  const [shouldDraw, setShouldDraw] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState(0.05);
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [axisLabels, setAxisLabels] = useState<{ label: string; col1: string; col2?: string; col3?: string; col4?: string; col5?: string; col6?: string; col7?: string; col8?: string; col9?: string; col10?: string; } | null>(null);
  const [labelFontSize, setLabelFontSize] = useState(14);
  const [pauseOnMinMax, setPauseOnMinMax] = useState(false);
  const [pauseDuration, setPauseDuration] = useState(1000);
  const [captureVideo, setCaptureVideo] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [showWatermark, setShowWatermark] = useState(true);
  const [drawCount, setDrawCount] = useState(0);
  const [barWidth, setBarWidth] = useState(40);
  const [barSpacing, setBarSpacing] = useState(20);
  const [showBarLabels, setShowBarLabels] = useState(true);
  const [shiftLargestLeft, setShiftLargestLeft] = useState(false);

  const colorOptions = [
    { value: 'white', label: 'White' },
    { value: 'black', label: 'Black' },
    { value: '#f0f0f0', label: 'Light Grey' },
    { value: '#404040', label: 'Dark Grey' }
  ];

  const parseData = (text: string) => {
    const lines = text.trim().split('\n');
    const parsedData: DataPoint[] = [];
    let index = 0;
    
    // Check if first row contains text in col1 column
    const firstLine = lines[0];
    // Split by either tabs or commas, and filter out empty values
    const firstValues = firstLine.split(/[\t,]+/).filter(val => val.trim() !== '');
    const hasHeaderRow = firstValues.length >= 2 && isNaN(parseFloat(firstValues[1]));
    
    // If first row is header, set axis labels and skip it
    if (hasHeaderRow) {
      const labels: any = {
        label: firstValues[0],
        col1: firstValues[1]
      };
      // Add optional column labels
      for (let i = 2; i <= 10; i++) {
        if (firstValues[i]) {
          labels[`col${i}`] = firstValues[i];
        }
      }
      setAxisLabels(labels);
      lines.shift(); // Remove the header row
    } else {
      setAxisLabels(null);
    }
    
    lines.forEach(line => {
      // Split by either tabs or commas, and filter out empty values
      const values = line.split(/[\t,]+/).filter(val => val.trim() !== '');
      if (values.length >= 2) {
        const label = values[0];
        const col1 = parseFloat(values[1]);
        
        if (!isNaN(col1)) {
          const dataPoint: DataPoint = {
            index,
            label,
            col1
          };
          
          // Add optional columns
          for (let i = 2; i <= 10; i++) {
            if (values[i] && !isNaN(parseFloat(values[i]))) {
              const key = `col${i}` as keyof DataPoint;
              dataPoint[key] = parseFloat(values[i]);
            }
          }
          
          parsedData.push(dataPoint);
          index++;
        }
      }
    });
    
    return parsedData;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
    setShouldDraw(false);
  };

  const handleDrawClick = () => {
    if (!inputText.trim()) {
      alert('Please enter some data first');
      return;
    }

    try {
      const parsedData = parseData(inputText);
      setData(parsedData);
      setShouldDraw(true);
      if (isPaid) {
        setDrawCount(prev => prev + 1);
      }
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Error parsing data');
    }
  };

  const handleClearClick = () => {
    setInputText('');
    setData([]);
    setShouldDraw(false);
    setAxisLabels(null);
  };

  const handleUseExampleData = async () => {
    try {
      const response = await fetch('/examplePopData.txt');
      if (!response.ok) {
        throw new Error('Failed to load example data');
      }
      const exampleText = await response.text();
      setInputText(exampleText);
      setAxisLabels(null); // Let the parser detect headers automatically
    } catch (error) {
      console.error('Error loading example data:', error);
      // Fallback to hardcoded data if file loading fails
      const fallbackText = `Population	USA	Indonesia	Pakistan	Nigeria	Brazil	Bangladesh	Russia
1950	148281550	69567619	37696264	37189369	53955359	39728535	102580107
1951	150598453	71019347	38215785	37903171	55591058	40548590	104151680
1952	152941727	72570827	38816777	38624425	57283338	41426885	105790045
1953	155451199	74207986	39488228	39347719	59025679	42329279	107493129
1954	158205873	75925325	40224090	40082479	60827590	43282032	109221068
1955	161136449	77741502	41023128	40839223	62701031	44315548	110971852`;
      setInputText(fallbackText);
      setAxisLabels(null);
    }
  };

  const handleRecordingComplete = (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    
    // Determine the correct extension based on the blob type and MIME type
    let extension = 'mp4'; // Default to MP4 for Twitter/X compatibility
    if (blob.type.includes('webm')) {
      extension = 'webm';
    } else if (blob.type.includes('mp4') || blob.type.includes('h264') || blob.type.includes('avc1')) {
      extension = 'mp4';
    }
    
    const filename = `animated-bar-graph.${extension}`;
    console.log('Downloading video as:', filename, 'blob type:', blob.type, 'extension:', extension);
    
    // Check if we're on iOS Safari
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    
    if (isIOS && isSafari) {
      // For iOS Safari, create a temporary link and trigger the share sheet
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      
      // Trigger the download first
      a.click();
      
      // After a short delay, show the share sheet
      setTimeout(() => {
        // Create a new blob URL for sharing
        const shareUrl = URL.createObjectURL(blob);
        
        // Check if Web Share API is available
        if (navigator.share && navigator.canShare) {
          const shareData = {
            title: 'Animated Bar Graph',
            text: 'Check out this animated bar graph I created!',
            url: shareUrl,
            files: [new File([blob], filename, { type: blob.type })]
          };
          
          if (navigator.canShare(shareData)) {
            navigator.share(shareData)
              .then(() => {
                console.log('Share sheet was shown successfully');
                URL.revokeObjectURL(shareUrl);
              })
              .catch((error) => {
                console.log('Error showing share sheet:', error);
                URL.revokeObjectURL(shareUrl);
              });
          } else {
            console.log('Web Share API not supported for files');
            URL.revokeObjectURL(shareUrl);
          }
        } else {
          console.log('Web Share API not available');
          URL.revokeObjectURL(shareUrl);
        }
      }, 1000); // 1 second delay to ensure download completes
      
      // Clean up the temporary link
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 2000);
    } else {
      // For other browsers, use the standard download approach
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
    
    setCaptureVideo(false);
  };

  const handlePaymentSuccess = () => {
    setIsPaid(true);
    setShowWatermark(false);
    setDrawCount(0);
  };

  const handlePaymentExpire = () => {
    setIsPaid(false);
    setShowWatermark(true);
  };

  return (
    <div style={{ 
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      <h1 style={{ 
        color: '#343a40',
        marginBottom: '20px',
        fontSize: '24px'
      }}>
        Animated Bar Graph
      </h1>

      {/* Text entry box */}
      <div style={{ 
        marginBottom: '20px',
        backgroundColor: '#f8f9fa',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        <h2 style={{ 
          color: '#495057',
          marginBottom: '15px',
          fontSize: '18px'
        }}>
          Enter Data
        </h2>
        <textarea
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder="Enter data in the format:&#10;Label,Value1,Value2,Value3&#10;Label,Value1,Value2,Value3&#10;Label,Value1,Value2,Value3"
          style={{
            width: '100%',
            height: '150px',
            padding: '12px',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            fontSize: '14px',
            fontFamily: 'monospace',
            resize: 'vertical',
            overflowX: 'auto',
            whiteSpace: 'nowrap'
          }}
        />
        <div style={{ 
          display: 'flex', 
          gap: '10px',
          marginTop: '15px'
        }}>
          <button
            onClick={handleClearClick}
            style={{
              padding: '8px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Clear
          </button>
          <button
            onClick={handleUseExampleData}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Use Example Data
          </button>
        </div>
      </div>

      {/* Graph controls */}
      <div style={{ 
        marginBottom: '20px',
        backgroundColor: '#f8f9fa',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        <h2 style={{ 
          color: '#495057',
          marginBottom: '15px',
          fontSize: '18px'
        }}>
          Graph Controls
        </h2>
        <div className="mobile-controls-grid" style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
          gap: '15px'
        }}>
          {/* Bar-specific controls */}
          <div className="mobile-control-item" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px'
          }}>
            <label 
              htmlFor="barWidth" 
              className="mobile-label"
              style={{ 
                fontSize: '14px',
                color: '#495057',
                minWidth: '80px'
              }}
            >
              Bar Width
            </label>
            <input
              type="range"
              id="barWidth"
              value={barWidth}
              onChange={(e) => setBarWidth(parseInt(e.target.value))}
              min="20"
              max="100"
              step="5"
              className="mobile-slider"
            />
            <span style={{ fontSize: '14px', color: '#495057' }}>{barWidth}px</span>
          </div>

          <div className="mobile-control-item" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px'
          }}>
            <label 
              htmlFor="barSpacing" 
              className="mobile-label"
              style={{ 
                fontSize: '14px',
                color: '#495057',
                minWidth: '80px'
              }}
            >
              Bar Spacing
            </label>
            <input
              type="range"
              id="barSpacing"
              value={barSpacing}
              onChange={(e) => setBarSpacing(parseInt(e.target.value))}
              min="0"
              max="50"
              step="5"
              className="mobile-slider"
            />
            <span style={{ fontSize: '14px', color: '#495057' }}>{barSpacing}px</span>
          </div>

          <div className="mobile-control-item" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px'
          }}>
            <label 
              htmlFor="showBarLabels" 
              className="mobile-label"
              style={{ 
                fontSize: '14px',
                color: '#495057'
              }}
            >
              Show Bar Labels
            </label>
            <input
              type="checkbox"
              id="showBarLabels"
              checked={showBarLabels}
              onChange={(e) => setShowBarLabels(e.target.checked)}
              className="mobile-checkbox"
            />
          </div>

          <div className="mobile-control-item" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px'
          }}>
            <label 
              htmlFor="shiftLargestLeft" 
              className="mobile-label"
              style={{ 
                fontSize: '14px',
                color: '#495057'
              }}
            >
              Shift Largest Left
            </label>
            <input
              type="checkbox"
              id="shiftLargestLeft"
              checked={shiftLargestLeft}
              onChange={(e) => setShiftLargestLeft(e.target.checked)}
              className="mobile-checkbox"
            />
          </div>

          {/* Common controls */}
          <div className="mobile-control-item" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px'
          }}>
            <label 
              htmlFor="animationSpeed" 
              className="mobile-label"
              style={{ 
                fontSize: '14px',
                color: '#495057'
              }}
            >
              Animation Speed
            </label>
            <input
              type="range"
              id="animationSpeed"
              value={animationSpeed}
              onChange={(e) => setAnimationSpeed(parseFloat(e.target.value))}
              min="0.01"
              max="0.2"
              step="0.01"
              className="mobile-slider"
            />
            <span style={{ fontSize: '14px', color: '#495057' }}>{animationSpeed.toFixed(2)}</span>
          </div>

          <div className="mobile-control-item" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px'
          }}>
            <label 
              htmlFor="backgroundColor" 
              className="mobile-label"
              style={{ 
                fontSize: '14px',
                color: '#495057'
              }}
            >
              Background
            </label>
            <select
              id="backgroundColor"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              className="mobile-select"
            >
              {colorOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="mobile-control-item" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px'
          }}>
            <label 
              htmlFor="labelFontSize" 
              className="mobile-label"
              style={{ 
                fontSize: '14px',
                color: '#495057'
              }}
            >
              Label Size
            </label>
            <input
              type="range"
              id="labelFontSize"
              value={labelFontSize}
              onChange={(e) => setLabelFontSize(parseInt(e.target.value))}
              min="8"
              max="24"
              step="1"
              className="mobile-slider"
            />
            <span style={{ fontSize: '14px', color: '#495057' }}>{labelFontSize}px</span>
          </div>
        </div>
      </div>

      {/* Draw Graph Button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center',
        gap: '20px',
        marginBottom: '20px'
      }}>
        <button
          onClick={handleDrawClick}
          style={{
            padding: '12px 24px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          Draw Graph
        </button>
        <button
          onClick={() => {
            setCaptureVideo(true);
            handleDrawClick();
          }}
          style={{
            padding: '12px 24px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          Draw Graph and Capture Video
        </button>
      </div>

      {/* Graph display */}
      <div style={{ 
        backgroundColor: '#f8f9fa',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        <h2 style={{ 
          color: '#495057',
          marginBottom: '15px',
          fontSize: '18px'
        }}>
          Graph Display
        </h2>
        <AnimatedBarGraph
          data={data}
          backgroundColor={backgroundColor}
          animationSpeed={animationSpeed}
          shouldDraw={shouldDraw}
          onRecordingComplete={handleRecordingComplete}
          axisLabels={axisLabels}
          barWidth={barWidth}
          barSpacing={barSpacing}
          showBarLabels={showBarLabels}
          shiftLargestLeft={shiftLargestLeft}
          labelFontSize={labelFontSize}
          captureVideo={captureVideo}
          showWatermark={showWatermark}
        />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center',
          marginTop: '20px'
        }}>
          <PaymentButton 
            onPaymentSuccess={handlePaymentSuccess} 
            onPaymentExpire={handlePaymentExpire}
            shouldDraw={shouldDraw}
            drawCount={drawCount}
          />
        </div>
      </div>
    </div>
  );
} 